import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { toast } from 'sonner';
import { Check, ArrowLeft, ArrowRight, Loader2, Rocket, ShieldCheck, CreditCard, Sparkles } from 'lucide-react';
import { useTheme } from 'contexts/ThemeContext';
import { profileService } from 'services/auth/authService';
import { getSubscriptions, SubscriptionPlan } from 'services/subscriptions/subscriptionService';
import { createStripeCheckoutSession } from 'services/subscriptions/paymentService';
import { getErrorMessage } from 'utils/errors';
import { RootState } from 'config/redux/store';

type BillingCycle = 'monthly' | 'annual';

const TOTAL_STEPS = 3;

// Display strings for plans live in the translation files, not in the DB.
// We map plan.subscription_name → an i18n key segment so EN/FR are guaranteed in sync —
// same pattern as the landing pricing section. DB still owns numbers (counts, limits, prices).
const planKeyMap: Record<string, string> = {
  Trial: 'trial',
  Starter: 'starter',
  Pro: 'pro',
  Elite: 'elite',
};

// Long-form "who this plan is for" copy, reused from the subscriptions card labels.
const planHighlightKey: Record<string, string> = {
  Trial: 'subscriptions.cardLabels.highlightTrial',
  Starter: 'subscriptions.cardLabels.highlightStarter',
  Pro: 'subscriptions.cardLabels.highlightPro',
  Elite: 'subscriptions.cardLabels.highlightElite',
};

const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  // True when the user previously had (and consumed) a trial — e.g. they deleted
  // their account and re-registered with the same email. The trial-gate in
  // stripe-payment.controller.js refuses to grant another trial in this state,
  // so the wizard hides the "7-day free trial" messaging that would otherwise
  // mislead them. Hydrated by App.tsx / ProtectedRoute via /validate-auth.
  const hasTrialed = useSelector((state: RootState) => state.user.hasTrialed);

  const [step, setStep] = useState<number>(1);
  const [language, setLanguage] = useState<'en' | 'fr'>((i18n.language?.startsWith('fr') ? 'fr' : 'en'));
  const [savingLanguage, setSavingLanguage] = useState(false);

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const [submitting, setSubmitting] = useState(false);

  // Theme tokens
  const pageBg = isDark ? '#0F172B' : '#F8FAFC';
  const cardBg = isDark ? '#1D293D' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#0F172B';
  const subtitleColor = isDark ? '#94A3B8' : '#64748B';
  const accent = '#2B7FFF';
  const accentGradient = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';

  // Load plans on mount (so step 2 is instant when user reaches it)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getSubscriptions();
        if (mounted) {
          // Sort by tier so cheapest plan is first
          const sorted = [...(res.plans || [])].sort((a, b) => (a.tier || 0) - (b.tier || 0));
          setPlans(sorted);
        }
      } catch {
        if (mounted) toast.error('Failed to load plans');
      } finally {
        if (mounted) setLoadingPlans(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ---- Step 1 actions ----
  const handleLanguageContinue = async () => {
    setSavingLanguage(true);
    try {
      await i18n.changeLanguage(language);
      await profileService.setPreferredLanguage(language);
      setStep(2);
    } catch {
      toast.error(t('onboarding.errors.languageSaveFailed', 'Could not save language. Try again.'));
    } finally {
      setSavingLanguage(false);
    }
  };

  // ---- Step 3 action: redirect to Stripe Checkout ----
  const handleCheckout = async () => {
    const plan = plans.find(p => p._id === selectedPlanId);
    if (!plan) return;

    const stripePriceId = billingCycle === 'annual'
      ? plan.stripe_price_ids?.annual
      : plan.stripe_price_ids?.monthly;

    if (!stripePriceId) {
      toast.error(t('onboarding.errors.stripeNotConfigured', 'This plan is not available yet. Please contact support.'));
      return;
    }

    setSubmitting(true);
    try {
      const session = (await createStripeCheckoutSession(plan._id, stripePriceId, billingCycle)) as { url?: string; mode?: string };
      if (session.url) {
        window.location.href = session.url;
      } else if (session.mode === 'plan_switched') {
        toast.success(t('onboarding.planSwitched', 'Plan switched successfully.'));
        navigate('/dashboard');
      } else {
        toast.error(t('onboarding.errors.checkoutFailed', 'Checkout failed. Please try again.'));
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('onboarding.errors.checkoutFailed', 'Checkout failed.')));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPlan = useMemo(() => plans.find(p => p._id === selectedPlanId) || null, [plans, selectedPlanId]);

  const priceFor = (plan: SubscriptionPlan, cycle: BillingCycle): number => {
    if (cycle === 'annual') {
      return (plan.price?.amount_4weeks_annual ?? plan.price?.amount_annually) || 0;
    }
    return (plan.price?.amount_4weeks ?? plan.price?.amount_monthly) || 0;
  };

  const yearlyTotalFor = (plan: SubscriptionPlan): number => {
    return plan.price?.amount_yearly || (priceFor(plan, 'annual') * 13);
  };

  return (
    <div
      className="min-h-screen flex flex-col font-geist"
      style={{ background: pageBg }}
    >
      {/* Header */}
      <header
        className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ borderBottom: `1px solid ${cardBorder}`, background: cardBg }}
      >
        <span style={{ fontSize: '16px', fontWeight: 700, color: titleColor }}>
          Famous<span style={{ fontWeight: 400 }}>Clicker</span>
        </span>
        <button
          onClick={() => navigate('/logout')}
          style={{
            fontSize: '13px',
            color: subtitleColor,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '6px 12px',
          }}
        >
          {t('onboarding.signOut', 'Sign out')}
        </button>
      </header>

      {/* Progress bar */}
      <div style={{ height: '3px', background: cardBorder, position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${(step / TOTAL_STEPS) * 100}%`,
            background: accentGradient,
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* Body */}
      <main className="flex-1 overflow-y-auto" style={{ padding: '40px 16px' }}>
        <div style={{ maxWidth: step === 2 ? '1100px' : '640px', margin: '0 auto' }}>
          {step === 1 && (
            <StepLanguage
              isDark={isDark}
              titleColor={titleColor}
              subtitleColor={subtitleColor}
              cardBg={cardBg}
              cardBorder={cardBorder}
              accent={accent}
              language={language}
              onSelectLanguage={setLanguage}
              hasTrialed={hasTrialed}
            />
          )}

          {step === 2 && (
            <StepPlan
              isDark={isDark}
              titleColor={titleColor}
              subtitleColor={subtitleColor}
              cardBg={cardBg}
              cardBorder={cardBorder}
              accent={accent}
              loadingPlans={loadingPlans}
              plans={plans}
              language={language}
              selectedPlanId={selectedPlanId}
              billingCycle={billingCycle}
              onSelectPlan={setSelectedPlanId}
              onChangeBillingCycle={setBillingCycle}
              priceFor={priceFor}
              t={t}
              hasTrialed={hasTrialed}
            />
          )}

          {step === 3 && selectedPlan && (
            <StepReview
              isDark={isDark}
              titleColor={titleColor}
              subtitleColor={subtitleColor}
              cardBg={cardBg}
              cardBorder={cardBorder}
              accent={accent}
              plan={selectedPlan}
              billingCycle={billingCycle}
              priceFor={priceFor}
              yearlyTotalFor={yearlyTotalFor}
              t={t}
              hasTrialed={hasTrialed}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ borderTop: `1px solid ${cardBorder}`, background: cardBg }}
      >
        <button
          onClick={() => (step === 1 ? navigate('/logout') : setStep((s) => Math.max(1, s - 1)))}
          disabled={submitting || savingLanguage}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 20px',
            fontSize: '14px', fontWeight: 500,
            color: titleColor,
            background: 'transparent',
            border: `1px solid ${cardBorder}`,
            borderRadius: '8px',
            cursor: (submitting || savingLanguage) ? 'not-allowed' : 'pointer',
            opacity: (submitting || savingLanguage) ? 0.5 : 1,
          }}
        >
          <ArrowLeft size={16} />
          {step === 1 ? t('onboarding.cancel', 'Sign out') : t('onboarding.back', 'Back')}
        </button>

        <span style={{ fontSize: '13px', color: subtitleColor }}>
          {t('onboarding.stepIndicator', 'Step {{current}} of {{total}}', { current: step, total: TOTAL_STEPS })}
        </span>

        {step === 1 && (
          <button
            onClick={handleLanguageContinue}
            disabled={savingLanguage}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 24px',
              fontSize: '14px', fontWeight: 600,
              color: '#FFFFFF',
              background: accentGradient,
              border: 'none', borderRadius: '8px',
              cursor: savingLanguage ? 'not-allowed' : 'pointer',
              opacity: savingLanguage ? 0.7 : 1,
            }}
          >
            {savingLanguage ? <Loader2 size={16} className="animate-spin" /> : null}
            {t('onboarding.continue', 'Continue')}
            <ArrowRight size={16} />
          </button>
        )}

        {step === 2 && (
          <button
            onClick={() => setStep(3)}
            disabled={!selectedPlanId}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 24px',
              fontSize: '14px', fontWeight: 600,
              color: '#FFFFFF',
              background: selectedPlanId ? accentGradient : (isDark ? '#314158' : '#CBD5E1'),
              border: 'none', borderRadius: '8px',
              cursor: selectedPlanId ? 'pointer' : 'not-allowed',
            }}
          >
            {t('onboarding.continue', 'Continue')}
            <ArrowRight size={16} />
          </button>
        )}

        {step === 3 && (
          <button
            onClick={handleCheckout}
            disabled={submitting || !selectedPlanId}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 24px',
              fontSize: '14px', fontWeight: 600,
              color: '#FFFFFF',
              background: accentGradient,
              border: 'none', borderRadius: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            {t('onboarding.continueToPayment', 'Continue to Payment')}
            <ArrowRight size={16} />
          </button>
        )}
      </footer>
    </div>
  );
};

// Localised copy for the wizard. Switches as soon as the user picks a language card.
// Two variants per language:
//   - trial: default — for users eligible for a 7-day free trial
//   - returning: for users who previously consumed their trial (has_trialed=true)
//     — re-registered same email, cancelled paid plan, etc. They will be charged
//     immediately on Stripe checkout, so "free trial" language would be misleading.
const wizardCopy = {
  en: {
    trial: {
      trialBadge: '7-DAY FREE TRIAL',
      heroTitle: 'Start growing on autopilot',
      heroSubtitleBefore: 'Choose your language to begin. Your 7-day free trial starts as soon as you pick a plan — ',
      heroSubtitleStrong: 'no charge for 7 days',
      heroSubtitleAfter: ', cancel anytime.',
      chipCancel: 'Cancel anytime',
      chipNoCharge: 'No charge for 7 days',
      chipSecured: 'Secured by Stripe',
    },
    returning: {
      trialBadge: 'WELCOME BACK',
      heroTitle: 'Pick up where you left off',
      heroSubtitleBefore: 'Choose your language to begin. Pick a plan to reactivate your Famous Clicker account — ',
      heroSubtitleStrong: 'cancel anytime',
      heroSubtitleAfter: '.',
      chipCancel: 'Cancel anytime',
      chipNoCharge: null, // hide the "no charge for 7 days" chip — returning users ARE charged immediately
      chipSecured: 'Secured by Stripe',
    },
    languageEn: 'English',
    languageEnSub: 'United States, UK, etc.',
    languageFr: 'Français',
    languageFrSub: 'France',
  },
  fr: {
    trial: {
      trialBadge: 'ESSAI GRATUIT DE 7 JOURS',
      heroTitle: 'Faites grandir votre audience en pilote automatique',
      heroSubtitleBefore: 'Choisissez votre langue pour commencer. Votre essai gratuit de 7 jours démarre dès que vous choisissez un plan — ',
      heroSubtitleStrong: 'aucun débit pendant 7 jours',
      heroSubtitleAfter: ', annulable à tout moment.',
      chipCancel: 'Annulable à tout moment',
      chipNoCharge: 'Aucun débit pendant 7 jours',
      chipSecured: 'Sécurisé par Stripe',
    },
    returning: {
      trialBadge: 'BIENVENUE',
      heroTitle: 'Reprenez là où vous vous étiez arrêté',
      heroSubtitleBefore: 'Choisissez votre langue pour commencer. Choisissez un plan pour réactiver votre compte Famous Clicker — ',
      heroSubtitleStrong: 'annulable à tout moment',
      heroSubtitleAfter: '.',
      chipCancel: 'Annulable à tout moment',
      chipNoCharge: null,
      chipSecured: 'Sécurisé par Stripe',
    },
    languageEn: 'English',
    languageEnSub: 'United States, UK, etc.',
    languageFr: 'Français',
    languageFrSub: 'France',
  },
} as const;

// ---- Step 1: Language ----
interface StepLanguageProps {
  isDark: boolean;
  titleColor: string;
  subtitleColor: string;
  cardBg: string;
  cardBorder: string;
  accent: string;
  language: 'en' | 'fr';
  onSelectLanguage: (code: 'en' | 'fr') => void;
  hasTrialed: boolean;
}

const StepLanguage: React.FC<StepLanguageProps> = ({ isDark, titleColor, subtitleColor, cardBg, cardBorder, accent, language, onSelectLanguage, hasTrialed }) => {
  const langCopy = wizardCopy[language as 'en' | 'fr'] || wizardCopy.en;
  // Pick the trial-vs-returning variant based on the user's trial-eligibility state.
  // hasTrialed=true means they've consumed their one allowed trial → suppress
  // "7-day free trial" messaging.
  const copy = hasTrialed ? langCopy.returning : langCopy.trial;
  const choices: { code: 'en' | 'fr'; label: string; subtitle: string; flag: string }[] = [
    { code: 'en', label: langCopy.languageEn, subtitle: langCopy.languageEnSub, flag: '🇬🇧' },
    { code: 'fr', label: langCopy.languageFr, subtitle: langCopy.languageFrSub, flag: '🇫🇷' },
  ];

  const trialBadgeBg = isDark
    ? 'linear-gradient(90deg, rgba(43, 127, 255, 0.15) 0%, rgba(173, 70, 255, 0.15) 100%)'
    : 'linear-gradient(90deg, #EFF6FF 0%, #F5F3FF 100%)';
  const trialBadgeBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const trialBadgeText = isDark ? '#BFDBFE' : '#1E3A8A';
  const chipBg = isDark ? '#1D293D' : '#F8FAFC';
  const chipBorder = isDark ? '#314158' : '#E2E8F0';

  return (
    <>
      {/* Hero illustration */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px', position: 'relative' }}>
        {/* Glow */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '8px',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(43, 127, 255, 0.25) 0%, rgba(173, 70, 255, 0.15) 40%, rgba(0,0,0,0) 70%)',
            filter: 'blur(12px)',
            pointerEvents: 'none',
          }}
        />
        {/* Rocket badge */}
        <div
          style={{
            position: 'relative',
            width: '96px',
            height: '96px',
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #2B7FFF 0%, #AD46FF 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 16px 40px rgba(43, 127, 255, 0.35)',
            marginBottom: '16px',
          }}
        >
          <Rocket size={44} color="#FFFFFF" strokeWidth={2.2} />
          {/* Animated sparkles */}
          <Sparkles
            size={16}
            color="#FBBF24"
            className="animate-pulse"
            style={{ position: 'absolute', top: '-6px', right: '-6px' }}
          />
          <Sparkles
            size={12}
            color="#FBBF24"
            className="animate-pulse"
            style={{ position: 'absolute', bottom: '-4px', left: '-4px', animationDelay: '0.6s' }}
          />
        </div>

        {/* Trial badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            background: trialBadgeBg,
            border: `1px solid ${trialBadgeBorder}`,
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 700,
            color: trialBadgeText,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          <Sparkles size={12} />
          {copy.trialBadge}
        </div>
      </div>

      <h1 style={{ fontSize: '32px', fontWeight: 800, color: titleColor, textAlign: 'center', marginBottom: '8px', letterSpacing: '-0.02em' }}>
        {copy.heroTitle}
      </h1>
      <p style={{ fontSize: '15px', color: subtitleColor, textAlign: 'center', marginBottom: '24px', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.6' }}>
        {copy.heroSubtitleBefore}<strong style={{ color: titleColor }}>{copy.heroSubtitleStrong}</strong>{copy.heroSubtitleAfter}
      </p>

      {/* Benefit chips. The "no charge for 7 days" chip is omitted for returning
          users (chipNoCharge is null in their copy variant). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginBottom: '32px' }}>
        {[
          { icon: <ShieldCheck size={14} color="#10B981" />, label: copy.chipCancel },
          { icon: <CreditCard size={14} color="#2B7FFF" />, label: copy.chipNoCharge },
          { icon: <ShieldCheck size={14} color="#AD46FF" />, label: copy.chipSecured },
        ].filter(b => !!b.label).map((b) => (
          <span
            key={b.label}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              color: subtitleColor,
              background: chipBg,
              border: `1px solid ${chipBorder}`,
              borderRadius: '999px',
            }}
          >
            {b.icon}
            {b.label}
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {choices.map((c) => {
          const selected = language === c.code;
          return (
            <button
              key={c.code}
              onClick={() => onSelectLanguage(c.code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '18px 20px',
                background: cardBg,
                border: `2px solid ${selected ? accent : cardBorder}`,
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                boxShadow: selected ? `0 4px 16px ${accent}33` : 'none',
              }}
            >
              <span style={{ fontSize: '28px' }}>{c.flag}</span>
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: '15px', fontWeight: 600, color: titleColor }}>{c.label}</span>
                <span style={{ display: 'block', fontSize: '12px', color: subtitleColor, marginTop: '2px' }}>{c.subtitle}</span>
              </span>
              {selected && <Check size={20} color={accent} />}
            </button>
          );
        })}
      </div>
    </>
  );
};

// ---- Step 2: Plan ----
type TFunc = ReturnType<typeof useTranslation>['t'];

interface StepPlanProps {
  isDark: boolean;
  titleColor: string;
  subtitleColor: string;
  cardBg: string;
  cardBorder: string;
  accent: string;
  loadingPlans: boolean;
  plans: SubscriptionPlan[];
  language: 'en' | 'fr';
  selectedPlanId: string | null;
  billingCycle: BillingCycle;
  onSelectPlan: (id: string) => void;
  onChangeBillingCycle: (cycle: BillingCycle) => void;
  priceFor: (plan: SubscriptionPlan, cycle: BillingCycle) => number;
  t: TFunc;
  hasTrialed: boolean;
}

const StepPlan: React.FC<StepPlanProps> = ({ isDark, titleColor, subtitleColor, cardBg, cardBorder, accent, loadingPlans, plans, language, selectedPlanId, billingCycle, onSelectPlan, onChangeBillingCycle, priceFor, t, hasTrialed }) => {
  return (
    <>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: titleColor, textAlign: 'center', marginBottom: '8px' }}>
        {t('onboarding.choosePlan', 'Choose your plan')}
      </h1>
      <p style={{ fontSize: '15px', color: subtitleColor, textAlign: 'center', marginBottom: '24px' }}>
        {hasTrialed
          ? t('onboarding.choosePlanSubtitleReturning', 'Pick a plan to reactivate your account. Cancel anytime.')
          : t('onboarding.choosePlanSubtitle', 'Start with a 7-day free trial. No charge until your trial ends. Cancel anytime.')}
      </p>

      {/* Billing cycle toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', background: isDark ? '#0F172B' : '#F1F5F9', padding: '4px', borderRadius: '12px' }}>
          {(['monthly', 'annual'] as const).map((cycle) => (
            <button
              key={cycle}
              onClick={() => onChangeBillingCycle(cycle)}
              style={{
                padding: '8px 18px',
                fontSize: '13px', fontWeight: 600,
                background: billingCycle === cycle ? cardBg : 'transparent',
                color: billingCycle === cycle ? titleColor : subtitleColor,
                border: 'none', borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: billingCycle === cycle ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {cycle === 'monthly' ? t('subscriptions.monthly', 'Monthly') : t('subscriptions.annual', 'Annual')}
              {cycle === 'annual' && <span style={{ fontSize: '10px', fontWeight: 700, background: '#10B981', color: '#FFFFFF', padding: '2px 6px', borderRadius: '4px' }}>-20%</span>}
            </button>
          ))}
        </div>
      </div>

      {loadingPlans ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: subtitleColor }}>
          <Loader2 size={28} className="animate-spin" style={{ display: 'inline-block' }} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '16px' }}>
          {plans.map((plan: SubscriptionPlan) => {
            const selected = selectedPlanId === plan._id;
            const price = priceFor(plan, billingCycle);
            const accentColors: Record<string, string> = {
              green: '#10B981',
              blue: '#2B7FFF',
              red: '#EF4444',
              gray: '#94A3B8',
            };
            const planAccent = accentColors[plan.color_indicator || ''] || accent;

            return (
              <button
                key={plan._id}
                onClick={() => onSelectPlan(plan._id)}
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  padding: '24px',
                  background: cardBg,
                  border: `2px solid ${selected ? planAccent : cardBorder}`,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  boxShadow: selected ? `0 6px 18px ${planAccent}33` : 'none',
                }}
              >
                {selected && (
                  <span style={{
                    position: 'absolute', top: '12px', right: '12px',
                    width: '24px', height: '24px', borderRadius: '50%',
                    background: planAccent, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check size={14} color="#FFFFFF" />
                  </span>
                )}
                <span style={{ fontSize: '12px', fontWeight: 700, color: planAccent, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {plan.subscription_name}
                </span>
                <span>
                  <span style={{ fontSize: '32px', fontWeight: 800, color: titleColor, display: 'block', lineHeight: 1 }}>
                    €{price}
                  </span>
                  <span style={{ fontSize: '12px', color: subtitleColor, marginTop: '4px', display: 'block' }}>
                    / 4 {t('subscriptions.weeks', 'weeks')}{billingCycle === 'annual' ? ` · ${t('subscriptions.annualBilling', 'annual')}` : ''}
                  </span>
                </span>
                <span style={{ fontSize: '13px', color: subtitleColor, lineHeight: '1.5' }}>
                  {/* Tagline from translation files (mirrors landing pricing).
                      Falls back to plan.tagline (DB) if no i18n key is mapped for this plan. */}
                  {planKeyMap[plan.subscription_name]
                    ? t(`landing.pricing.${planKeyMap[plan.subscription_name]}.target`)
                    : (typeof plan.tagline === 'string' ? plan.tagline : (plan.tagline?.[language as 'en' | 'fr'] || plan.tagline?.en || ''))}
                </span>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                    <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>
                      {t('onboarding.instagramAccount', { count: plan.number_of_accounts })}
                    </span>
                  </li>
                  {plan.features?.likes && (plan.features.likes.min > 0 || plan.features.likes.max > 0) && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {t('onboarding.automatedLikes', {
                          min: plan.features.likes.min,
                          max: plan.features.likes.max,
                          unit: t(plan.features.likes.unit === 'week' ? 'onboarding.unitWeek' : 'onboarding.unitDay')
                        })}
                      </span>
                    </li>
                  )}
                  {plan.features?.follow_unfollow?.enabled && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {plan.features.follow_unfollow.description
                          ? t('onboarding.smartFollowUnfollowDesc', { description: plan.features.follow_unfollow.description })
                          : t('onboarding.smartFollowUnfollow')}
                      </span>
                    </li>
                  )}
                  {plan.features?.ai_comments && (plan.features.ai_comments.min > 0 || plan.features.ai_comments.max > 0) && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {t('onboarding.aiComments', {
                          min: plan.features.ai_comments.min,
                          max: plan.features.ai_comments.max,
                          unit: t(plan.features.ai_comments.unit === 'week' ? 'onboarding.unitWeek' : 'onboarding.unitDay')
                        })}
                      </span>
                    </li>
                  )}
                  {plan.features?.reposting && plan.features.reposting.limit !== 0 && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {plan.features.reposting.limit > 0
                          ? t('onboarding.automaticRepostingLimit', {
                              limit: plan.features.reposting.limit,
                              unit: t(plan.features.reposting.unit === 'day' ? 'onboarding.unitDay' : 'onboarding.unitWeek')
                            })
                          : t('onboarding.automaticReposting')}
                      </span>
                    </li>
                  )}
                  {plan.features?.ai_auto_replies?.enabled && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {t('onboarding.aiAutoReplies')}
                        {plan.features.ai_auto_replies.features?.length > 0 && (
                          <span style={{ display: 'block', fontSize: '12px', color: subtitleColor, marginTop: '2px' }}>
                            {/* AI reply sub-features come from the DB as free text — look each one up
                                in the existing subscriptionPlans.features.aiAutoReplies namespace
                                (key = original string with spaces stripped). Falls back to the raw
                                DB value if a key isn't defined yet. */}
                            {plan.features.ai_auto_replies.features
                              .map((f: string) => t(`subscriptionPlans.features.aiAutoReplies.${f.replace(/\s+/g, '')}`, f))
                              .join(', ')}
                          </span>
                        )}
                      </span>
                    </li>
                  )}
                  {plan.setup_type && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {/* Discrete enum keys so EN/FR have proper grammar (e.g. "Configuration avancée"
                            rather than literal "Configuration advanced"). */}
                        {t(`onboarding.setup.${plan.setup_type}`, plan.setup_type.replace(/_/g, ' '))}
                      </span>
                    </li>
                  )}
                  {plan.volume_type && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {t(`onboarding.volume.${plan.volume_type}`, plan.volume_type.replace(/_/g, ' '))}
                      </span>
                    </li>
                  )}
                  {plan.support_level && (
                    <li style={{ display: 'flex', gap: '8px', fontSize: '13px', color: titleColor }}>
                      <Check size={16} color={planAccent} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        {t(`onboarding.support.${plan.support_level}`, plan.support_level.replace(/_/g, ' '))}
                      </span>
                    </li>
                  )}
                </ul>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

// ---- Step 3: Review ----
interface StepReviewProps {
  isDark: boolean;
  titleColor: string;
  subtitleColor: string;
  cardBg: string;
  cardBorder: string;
  accent: string;
  plan: SubscriptionPlan;
  billingCycle: BillingCycle;
  priceFor: (plan: SubscriptionPlan, cycle: BillingCycle) => number;
  yearlyTotalFor: (plan: SubscriptionPlan) => number;
  t: TFunc;
  hasTrialed: boolean;
}

const StepReview: React.FC<StepReviewProps> = ({ isDark, titleColor, subtitleColor, cardBg, cardBorder, accent, plan, billingCycle, priceFor, yearlyTotalFor, t, hasTrialed }) => {
  const price = priceFor(plan, billingCycle);
  const yearlyTotal = yearlyTotalFor(plan);
  // Pull descriptive copy from translations (i18n is authoritative for plan display strings)
  // rather than from plan.description in the DB — same pattern as the landing pricing section.
  // Falls back to an empty string for unknown plan names so we never render English to FR users.
  const highlightKey = planHighlightKey[plan.subscription_name] || '';
  const description = highlightKey ? t(highlightKey) : '';

  return (
    <>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: titleColor, textAlign: 'center', marginBottom: '8px' }}>
        {t('onboarding.review', 'Review your selection')}
      </h1>
      <p style={{ fontSize: '15px', color: subtitleColor, textAlign: 'center', marginBottom: '32px' }}>
        {hasTrialed
          ? t('onboarding.reviewSubtitleReturning', "You'll enter your card next. You'll be charged immediately to reactivate your account.")
          : t('onboarding.reviewSubtitle', "You'll enter your card next. We won't charge anything during your 7-day trial.")}
      </p>

      <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '14px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div>
            <span style={{ fontSize: '12px', fontWeight: 700, color: accent, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {plan.subscription_name}
            </span>
            <p style={{ fontSize: '14px', color: subtitleColor, marginTop: '6px', lineHeight: '1.5' }}>{description}</p>
          </div>
          <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '28px', fontWeight: 800, color: titleColor, display: 'block', lineHeight: 1 }}>€{price}</span>
            <span style={{ fontSize: '12px', color: subtitleColor }}>/ 4 {t('subscriptions.weeks', 'weeks')}</span>
          </span>
        </div>

        <div style={{ height: '1px', background: cardBorder }} />

        <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
            <dt style={{ color: subtitleColor }}>{t('onboarding.billingCycle')}</dt>
            <dd style={{ color: titleColor, fontWeight: 600 }}>
              {billingCycle === 'annual'
                ? t('onboarding.annualBilling', { total: yearlyTotal })
                : t('onboarding.monthlyBilling')}
            </dd>
          </div>
          {/* Free-trial row is shown ONLY when the user is eligible for a trial.
              Returning users (has_trialed=true) get charged immediately at checkout
              — showing "Free trial: 7 days" + "Due today: €0.00" would be wrong. */}
          {!hasTrialed && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <dt style={{ color: subtitleColor }}>{t('onboarding.freeTrial')}</dt>
              <dd style={{ color: '#10B981', fontWeight: 600 }}>{t('onboarding.sevenDays')}</dd>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
            <dt style={{ color: subtitleColor }}>{t('onboarding.dueToday')}</dt>
            <dd style={{ color: titleColor, fontWeight: 700 }}>{hasTrialed ? `€${price}` : '€0.00'}</dd>
          </div>
        </dl>

        <div
          style={{
            background: isDark
              ? 'linear-gradient(90deg, #1C398E 0%, #4C1D95 100%)'
              : 'linear-gradient(90deg, #EFF6FF 0%, #F5F3FF 100%)',
            border: `1px solid ${isDark ? '#1E3A8A' : '#BFDBFE'}`,
            borderRadius: '10px',
            padding: '14px 16px'
          }}
        >
          <p style={{ fontSize: '13px', color: isDark ? '#DBEAFE' : '#1E3A8A', margin: 0, lineHeight: '1.5' }}>
            {hasTrialed
              ? t('onboarding.cardOnFileNoticeReturning', { price, defaultValue: "Your card will be saved securely with Stripe. You'll be charged €{{price}} now and every 4 weeks unless you cancel." })
              : t('onboarding.cardOnFileNotice', { price })}
          </p>
        </div>
      </div>
    </>
  );
};

export default OnboardingWizard;
